import { useEffect, useState, useCallback } from "react";
import { Sun, Moon, Monitor } from "lucide-react";

const KEY = "jx-theme";
const ORDER = ["dark", "light", "auto"]; // cycle order

function systemPrefersDark() {
  return typeof window !== "undefined" && window.matchMedia
    && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function getStoredPref() {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "auto" ? v : "dark";
  } catch {
    return "dark";
  }
}

export function resolveTheme(pref) {
  if (pref === "auto") return systemPrefersDark() ? "dark" : "light";
  return pref === "light" ? "light" : "dark";
}

export function applyResolvedTheme(resolved) {
  const root = document.documentElement;
  if (resolved === "light") root.classList.add("light");
  else root.classList.remove("light");
  // keep the PWA/browser status bar colour in sync
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "light" ? "#ffffff" : "#0a0a0a");
}

export function useTheme() {
  const [pref, setPref] = useState(getStoredPref());

  // Apply + persist whenever the preference changes, and notify other toggles
  useEffect(() => {
    applyResolvedTheme(resolveTheme(pref));
    try {
      localStorage.setItem(KEY, pref);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent("jx-theme-change", { detail: pref }));
  }, [pref]);

  // Keep multiple ThemeToggle instances (nav + dashboard) in sync
  useEffect(() => {
    const onChange = (e) => {
      if (e.detail && e.detail !== pref) setPref(e.detail);
    };
    window.addEventListener("jx-theme-change", onChange);
    return () => window.removeEventListener("jx-theme-change", onChange);
  }, [pref]);

  // While on "auto", follow the OS theme live
  useEffect(() => {
    if (pref !== "auto" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyResolvedTheme(resolveTheme("auto"));
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [pref]);

  const cycle = useCallback(
    () => setPref((p) => ORDER[(ORDER.indexOf(p) + 1) % ORDER.length]),
    []
  );

  return { pref, cycle };
}

const META = {
  dark: { Icon: Moon, label: "Dark" },
  light: { Icon: Sun, label: "Light" },
  auto: { Icon: Monitor, label: "Auto" },
};

export default function ThemeToggle({ className = "", compact = false }) {
  const { pref, cycle } = useTheme();
  const { Icon, label } = META[pref];
  const title = `Theme: ${label} — click to switch (Dark → Light → Auto)`;
  if (compact) {
    return (
      <button
        data-testid="theme-toggle-nav"
        data-theme-pref={pref}
        onClick={cycle}
        title={title}
        aria-label={`Colour theme: ${label}. Click to change.`}
        className={`w-8 h-8 surface-card hover:border-primary-accent flex items-center justify-center ${className}`}
      >
        <Icon size={14} />
      </button>
    );
  }
  return (
    <button
      data-testid="theme-toggle"
      data-theme-pref={pref}
      onClick={cycle}
      title={title}
      aria-label={`Colour theme: ${label}. Click to change.`}
      className={`surface-card px-4 py-3 font-display tracking-widest text-sm uppercase text-primary-accent hover:border-primary-accent transition-colors flex items-center gap-2 ${className}`}
    >
      <Icon size={16} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
